import React from 'react'
import { Container, Typography, Divider } from '@cleartrip/ct-design-components'
import NewFareRulesFeeTxt from './NewFareRulesFeeTxt'
import NewFareRulesLoader from './NewFareRulesLoader'
import { InfoWarning } from '@/assets'
interface ChargeInfo {
    type: string
    amount: number
}
interface RuleInfo {
    benefitType: string
    charges: ChargeInfo[]
}
interface NewFareRulesProps {
    penaltiesData?: any,
    isLoading?: boolean,
    isIntlSpecialRT?: boolean
}

const NewFareRules: React.FC<NewFareRulesProps> = ({
    penaltiesData = {},
    isLoading = false,
    isIntlSpecialRT = false
}) => {
    // Constants for layout structure
    const fareRulesHeader = 'Standard fare (Price per traveler)'
    // Extract dynamic benefit types and charges
    const extractFareRulesData = (journeyData: any): RuleInfo[] => {
        if (!journeyData?.fareBenefits) return []
        const fareRulesData: RuleInfo[] = []
        Object.keys(journeyData.fareBenefits).forEach((fareKey) => {
            const fareData = journeyData.fareBenefits[fareKey]
            const subTravelOptionBenefits = fareData?.subTravelOptionBenefits || {}
            // Iterate through subTravelOptionBenefits to get benefit types
            Object.keys(subTravelOptionBenefits).forEach((benefitKey) => {
                const benefitData = subTravelOptionBenefits[benefitKey]
                const { penalties = [] } = benefitData?.benefits || {}
                for (let i = 0; i < penalties.length; i++) {
                    const charges = penalties?.[i]?.benefitList?.[0]?.details?.fareRules?.chargeInfos?.[0]?.chargesBreakup || []
                    const benefitType = penalties?.[i]?.benefitList?.[0]?.benefitType || ''
                    if (benefitType) {
                        // Filter charges to only show Airline and Cleartrip fees
                        const filteredCharges = charges
                            .filter((charge: any) => {
                                const chargeType = charge.type || ''
                                return chargeType.toLowerCase() === 'airline charges' || chargeType.toLowerCase() === 'cleartrip charges'
                            })
                            .map((charge: any) => {
                                return {
                                    type: (charge.type || '').toLowerCase() === 'airline charges' ? 'Airline fee' : 'Cleartrip fee',
                                    amount: Math.abs(charge?.amount || 0) // Use absolute value for display
                                }
                            })

                        if (filteredCharges.length > 0) {
                            fareRulesData.push({
                                benefitType,
                                charges: filteredCharges
                            })
                        }
                    }
                }

            })
        })

        return fareRulesData
    }

    // Get fare rules data for onward journey (we'll use onward data for now)
    const fareRulesInfo = extractFareRulesData(penaltiesData)

    // Create tabs dynamically based on available benefit types
    const fareRulesTab = fareRulesInfo.map((info: RuleInfo) => {
        const { benefitType } = info
        switch (benefitType) {
            case 'AMEND':
                return 'Date change fee'
            case 'CANCEL':
                return 'Cancellation fee'
            default:
                return benefitType ? benefitType.charAt(0).toUpperCase() + benefitType.slice(1).toLowerCase() + ' fee' : ''
        }
    })

    const isRefundable = fareRulesInfo.length > 0
    // Show loading state
    if (isLoading) {
        return <NewFareRulesLoader fareRulesTab={fareRulesTab} />
    }

    return (
        <>
            <Container className={`px-6  ${isIntlSpecialRT ? 'pt-2' : 'pt-6'}`}>
                {isIntlSpecialRT && (
                    <Container
                        className="p-3 br-12 flex flex-middle flex-between gap-8"
                        css={{
                            backgroundColor: '#FFF5DD'
                        }}>
                        <Container className="mr-2" css={{ minWidth: '16px' }}>
                            <InfoWarning />
                        </Container>
                        <p style={{ fontSize: '12px', lineHeight: '16px' }}>
                            {' '}
                            <span style={{ fontWeight: '600' }}>
                            Partial cancellation is not allowed for this flight combination
                            </span>
                            <span>
                            {' '}
                            Cancellation applies to full journey. The refundable amount is based on the total round-trip price
                            </span>
                        </p>
                    </Container>
                )}
                <Container className={`${isIntlSpecialRT ? 'mt-4' : ''} br-12 ba-solid bc-line-500 o-hidden`}>

                    {/* Header Section */}
                    <Container className="py-3 px-4" backgroundColor="#F3F3F3">
                        <Typography variant="HM3">{fareRulesHeader}</Typography>
                    </Container>

                    {/* Content Section */}
                    {!isRefundable ? (
                        <Typography variant="HM3" className="mt-10 mb-10 px-4">
                            Non-refundable
                        </Typography>
                    ) : (
                        fareRulesTab?.map((item: string, index: number) => {
                            const ruleInfo = fareRulesInfo[index]
                            const charges = ruleInfo?.charges || []
                            return (
                                <React.Fragment key={index}>
                                    <Container className="flex">
                                        {/* Left Column - Rule Name */}
                                        <Container width="30%" className="pl-4 py-4">
                                            <Typography variant="B2">{item}</Typography>
                                        </Container>

                                        {/* Right Column - Fee Details */}
                                        <Container
                                            width="70%"
                                            className="pl-4 pb-4 pt-2"
                                            borderLeft="1px solid #E6E6E6"
                                        >
                                            {/* Dynamic fee structure from API */}
                                            {charges.length > 0 ? (
                                                charges.map((charge: ChargeInfo, chargeIndex: number) => (
                                                    <NewFareRulesFeeTxt
                                                        key={chargeIndex}
                                                        feeName={charge.type || 'Fee'}
                                                        value={`₹${Math.abs(charge.amount || 0)}`}
                                                        valueColor="subheading"
                                                    />
                                                ))
                                            ) : (
                                                <NewFareRulesFeeTxt
                                                    feeName="No charges"
                                                    value="₹0"
                                                    valueColor="subheading"
                                                />
                                            )}
                                        </Container>
                                    </Container>

                                    {/* Divider between rows (except last one) */}
                                    {index !== fareRulesTab.length - 1 && (
                                        <Divider borderColor="#E6E6E6" />
                                    )}
                                </React.Fragment>
                            )
                        })
                    )}
                </Container>

                {/* Bottom Note Section */}
                <Typography variant="P3" color="subheading" className="pt-4 pb-10">
                    Cancellation/Flight change charges are indicative. We will stop accepting cancellation/change requests 4 - 72 hours before departure of the flight, depending on the airline.
                </Typography>
            </Container>
        </>
    )
}

export default NewFareRules
